import { LinearGradient } from "expo-linear-gradient";
// VerseCard.tsx
import { View, Text, Platform, Animated, TextStyle, TouchableOpacity, Image, StyleSheet } from "react-native";

import { useLanguageStore } from '../../features/quiz-core/languageStore';
import { t } from '../../i18n/translations';

import styles from "./styles";

interface VerseCardProps {
  fadeAnim: Animated.Value;
  slideAnim: Animated.Value;
  scaleAnim: Animated.Value;
  isCardHovered: boolean;
  setCardHovered: (hovered: boolean) => void;
  reduceMotion: boolean;
  compact?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

export default function VerseCard({
  fadeAnim,
  slideAnim,
  scaleAnim,
  isCardHovered,
  setCardHovered,
  reduceMotion: _reduceMotion,
  compact = false,
  isExpanded = false,
  onToggleExpand,
}: VerseCardProps) {
  const { language } = useLanguageStore();
  const verseText = t('bibleVerses.acts_17_11.text', language);
  const versePreview = `${verseText.slice(0, 74).trimEnd()}...`;
  const showCompactPreview = compact && !isExpanded;
  const animatedStyle = {
    opacity: fadeAnim,
    transform: [
      { translateY: slideAnim },
      { scale: scaleAnim },
    ],
    ...(isCardHovered && Platform.OS === 'web' ? {
      outlineWidth: 2,
      outlineColor: '#4F46E566',
      outlineStyle: 'solid',
    } : {}),
  };

  const quoteStyle: TextStyle = {
    fontSize: 48,
    fontWeight: '700',
    color: '#2A1605',
    opacity: 0.15,
    position: 'absolute',
    top: -4,
    left: 0,
  };

  return (
    <TouchableOpacity
      onPress={compact ? onToggleExpand : undefined}
      onPressIn={() => setCardHovered(true)}
      onPressOut={() => setCardHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={`Inspirational Bible verse: ${t('bibleVerses.acts_17_11.reference', language)}`}
      activeOpacity={1}
    >
      <Animated.View
        style={[
          styles.verseCard,
          compact && {
            minHeight: 250,
            paddingVertical: 22,
            paddingHorizontal: 22,
          },
          animatedStyle,
        ]}
      >
        <Image
          source={require('../../../assets/iqes/parchment-surface.png')}
          style={[StyleSheet.absoluteFillObject, { opacity: 0.9 }]}
          resizeMode="cover"
        />
        <LinearGradient
          colors={['rgba(255, 232, 163, 0.24)', 'rgba(58, 25, 5, 0.16)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: 28,
          }}
        />
        <Text style={quoteStyle}>“</Text>
        <View style={{ alignItems: 'center', width: '100%', zIndex: 2 }}>
          <View
            style={{
              paddingHorizontal: 14,
              paddingVertical: 8,
              borderRadius: 999,
              backgroundColor: 'rgba(18, 11, 4, 0.76)',
              borderWidth: 1,
              borderColor: 'rgba(255, 213, 74, 0.52)',
              marginBottom: 18,
            }}
          >
            <Text style={styles.verseReference}>{t('bibleVerses.acts_17_11.reference', language)}</Text>
          </View>
          <Text
            style={[
              styles.verseText,
              compact && {
                fontSize: 23,
                lineHeight: 32,
                maxWidth: 300,
              },
            ]}
            numberOfLines={showCompactPreview ? 3 : undefined}
          >
            {showCompactPreview ? versePreview : verseText}
          </Text>
          {compact && (
            <Text
              style={{
                marginTop: 14,
                fontSize: 12,
                fontWeight: '600',
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                color: '#56300B',
              }}
            >
              {isExpanded
                ? t('landing.verseCollapseLabel', language)
                : t('landing.versePreviewLabel', language)}
            </Text>
          )}
        </View>
      </Animated.View>
    </TouchableOpacity>
  );
}
